import { useRef, useEffect } from 'react';

/**
 * DecimalInput - A numeric input that doesn't lose cursor position.
 * 
 * Uses uncontrolled input (defaultValue) to prevent React re-render from
 * resetting cursor position. Syncs value from parent only when NOT focused.
 */
export function DecimalInput({ value, onChange, placeholder, className, ...props }) {
  const ref = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  // Keep onChange ref current without causing re-renders
  onChangeRef.current = onChange;

  // Sync from parent only when input is NOT focused (e.g., after form reset)
  useEffect(() => {
    valueRef.current = value;
    if (ref.current && ref.current !== document.activeElement) {
      ref.current.value = value ?? '';
    }
  }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === '' || /^\d*\.?\d*$/.test(v)) {
      valueRef.current = v;
      onChangeRef.current(v);
    } else {
      // Reject invalid input, restore last good value
      e.target.value = valueRef.current ?? '';
    }
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      defaultValue={value ?? ''}
      onChange={handleChange}
      placeholder={placeholder || '0.000'}
      className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
      {...props}
    />
  );
}
